import type { ReactNode } from "react";
import { ChevronsUpDown, FlaskConical, PanelLeftClose, PanelLeftOpen, type LucideIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { fill } from "../../lib/fill";
import { useKitCopy } from "../../lib/kit-copy";
import type { KitLabels } from "../../lib/kit-labels";
import { Logo } from "../primitives/logo";

export interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  href?: string;
  badge?: number;
}

/** Sidebar (05 §4): expanded or collapsed (icons with accessible names), workspace switcher at the bottom. */
export function Sidebar({ items, active, collapsed, onCollapsedChange, footer }: { items: NavItem[]; active: string; collapsed?: boolean; onCollapsedChange?: (v: boolean) => void; footer?: ReactNode }) {
  const l = useKitCopy().labels;
  return (
    <aside aria-label={l.sidebar} className={cn("hidden shrink-0 border-r border-border bg-bg lg:block", collapsed ? "w-18" : "w-sidebar")}>
      <div className="sticky top-0 flex h-screen flex-col">
        <div className={cn("flex h-14 items-center", collapsed ? "justify-center" : "justify-between px-5")}>
          <Logo withWord={!collapsed} />
          {onCollapsedChange && !collapsed && (
            <button type="button" aria-label={l.collapseSidebar} onClick={() => onCollapsedChange(true)} className="inline-grid size-8 place-items-center rounded-btn text-text-muted hover:bg-surface-2">
              <PanelLeftClose aria-hidden className="size-4" />
            </button>
          )}
        </div>
        {onCollapsedChange && collapsed && (
          <button type="button" aria-label={l.expandSidebar} onClick={() => onCollapsedChange(false)} className="mx-auto inline-grid size-9 place-items-center rounded-btn text-text-muted hover:bg-surface-2">
            <PanelLeftOpen aria-hidden className="size-4" />
          </button>
        )}
        <nav aria-label={l.mainNav} className="flex-1 space-y-0.5 px-3 py-2">
          {items.map((n) => {
            const on = n.id === active;
            return (
              <a
                key={n.id}
                href={n.href ?? "#"}
                aria-current={on ? "page" : undefined}
                aria-label={collapsed ? `${n.label}${n.badge ? `, ${fill(l.countOpen, { count: n.badge })}` : ""}` : undefined}
                className={cn(
                  "relative flex h-9 items-center gap-3 rounded-btn px-3 text-sm transition-colors duration-(--duration-hover)",
                  collapsed && "justify-center px-0",
                  on ? "bg-surface font-semibold text-brand-600 shadow-raised" : "text-text-muted hover:bg-surface-2 hover:text-text",
                )}
              >
                <n.icon aria-hidden className="size-4.5 shrink-0" strokeWidth={1.75} />
                {!collapsed && <span className="flex-1">{n.label}</span>}
                {n.badge ? (
                  <span className={cn("tnum grid h-5 min-w-5 place-items-center rounded-full bg-exploited-bg px-1.5 text-2xs font-semibold text-exploited", collapsed && "absolute -right-1 -top-1")}>
                    {n.badge}
                    {!collapsed && <span className="sr-only"> {l.open}</span>}
                  </span>
                ) : null}
              </a>
            );
          })}
        </nav>
        {footer && <div className="space-y-2 border-t border-border p-3">{footer}</div>}
      </div>
    </aside>
  );
}

/** Classes of the workspace switcher's button: shared by the switcher (workspace-switcher.tsx) and its placeholder. */
export const workspaceSwitcherTriggerClass = "flex w-full items-center gap-2 rounded-btn border border-border bg-surface px-3 py-2 text-left text-xs hover:bg-surface-2";

export interface SwitcherWorkspace {
  id: string;
  name: string;
  company: string;
  test?: boolean;
}

/** What the switcher's button shows: the marker, name, company and the chevron. */
export function WorkspaceSwitcherFace({ current, labels }: { current: SwitcherWorkspace; /** `switchWorkspace`; default from `KitCopyProvider`. */ labels?: Partial<KitLabels> }) {
  const l = useKitCopy(undefined, labels).labels;
  return (
    <>
      {current.test ? <FlaskConical aria-hidden className="size-4 text-medium" /> : <span aria-hidden className="size-2 rounded-full bg-ok" />}
      <span className="min-w-0 flex-1">
        <span className="block font-semibold">{current.name}</span>
        <span className="block truncate text-text-muted">{current.company}</span>
      </span>
      <ChevronsUpDown aria-hidden className="size-3.5 text-text-muted" />
      <span className="sr-only">{l.switchWorkspace}</span>
    </>
  );
}

/**
 * Looks exactly like the workspace switcher's button, without the menu (REQUESTS #110): the Suspense fallback while
 * `@brevalt/ui/workspace-switcher` (Radix DropdownMenu, popper) loads, so the shell's first chunk stays light.
 * Not in the Tab order (it does nothing yet); `onIntent` fires on hover or touch, to start loading the real switcher.
 */
export function WorkspaceSwitcherPlaceholder({ current, onIntent, labels }: { current: SwitcherWorkspace; onIntent?: () => void; labels?: Partial<KitLabels> }) {
  return (
    <button type="button" tabIndex={-1} aria-busy="true" onPointerEnter={onIntent} onPointerDown={onIntent} className={workspaceSwitcherTriggerClass}>
      <WorkspaceSwitcherFace current={current} labels={labels} />
    </button>
  );
}

/** Top bar (04 §0): search, the deadline pill, bell, assistant, plan badge. Opaque surface (no glass in the app). */
export function TopBar({ search, deadline, actions, mobileBrand }: { search?: ReactNode; deadline?: ReactNode; actions?: ReactNode; mobileBrand?: boolean }) {
  return (
    <header className="sticky top-0 z-(--z-sticky) flex h-14 items-center gap-2 border-b border-border bg-surface px-3 lg:gap-3 lg:px-6">
      {mobileBrand && (
        <span className="lg:hidden">
          <Logo withWord={false} />
        </span>
      )}
      {search && <div className="hidden max-w-sm flex-1 lg:block">{search}</div>}
      <div className="flex flex-1 justify-center lg:flex-none">{deadline}</div>
      <div className="ml-auto flex items-center gap-1">{actions}</div>
    </header>
  );
}

/** Mobile tab bar (04 §0): Dashboard, Cases, Findings, More. Opaque; safe-area padding. */
export function MobileTabBar({ items, active }: { items: NavItem[]; active: string }) {
  const l = useKitCopy().labels;
  return (
    <nav aria-label={l.mainNav} className="fixed inset-x-0 bottom-0 z-(--z-nav) grid grid-cols-4 border-t border-border bg-surface pb-[env(safe-area-inset-bottom)] lg:hidden">
      {items.map((t) => {
        const on = t.id === active;
        return (
          <a key={t.id} href={t.href ?? "#"} aria-current={on ? "page" : undefined} className={cn("relative flex h-16 flex-col items-center justify-center gap-1 text-2xs", on ? "font-semibold text-brand-600" : "text-text-muted")}>
            <span className="relative">
              <t.icon aria-hidden className="size-5" strokeWidth={1.75} />
              {t.badge ? <span aria-hidden className="absolute -right-1.5 -top-1 size-2 rounded-full bg-exploited" /> : null}
            </span>
            {t.label}
            {t.badge ? <span className="sr-only">, {fill(l.countOpen, { count: t.badge })}</span> : null}
          </a>
        );
      })}
    </nav>
  );
}
